/**
 * useAnimatedColorTransition — smoothly interpolates style colours between
 * discrete states.
 *
 * Consumers describe *what* should transition (a numeric state index and
 * colour arrays per style property). The hook hides the animation engine
 * (reanimated's `useDerivedValue`, `withTiming`, `interpolateColor`).
 *
 * @example
 * ```tsx
 * const alarmState = isActivated ? 2 : isArmed ? 1 : 0;
 *
 * const style = useAnimatedColorTransition(alarmState, {
 *   backgroundColor: [warningBg, purpleBg, errorBg],
 *   borderColor:     [warningBdr, purpleBdr, errorBdr],
 * });
 *
 * return <AnimatedView style={[baseStyle, style]} />;
 * ```
 */
import { useMemo } from "react";
import { Easing, interpolateColor, useAnimatedStyle, useDerivedValue, withTiming } from "react-native-reanimated";

// ---------------------------------------------------------------------------
// Defaults
// ---------------------------------------------------------------------------

const DEFAULT_DURATION = 500;
const DEFAULT_EASING = Easing.out(Easing.ease);

// ---------------------------------------------------------------------------
// Public types
// ---------------------------------------------------------------------------

/**
 * Maps animatable style properties to arrays of colour values — one entry per
 * state index.  For example, `{ backgroundColor: ['#ff0', '#0f0', '#f00'] }`
 * means state 0 → yellow, state 1 → green, state 2 → red.
 */
export interface ColorTransitionStops {
  readonly backgroundColor?: readonly string[];
  readonly borderColor?: readonly string[];
}

export interface ColorTransitionConfig {
  /** Transition duration in ms. Defaults to 500. */
  readonly duration?: number;
}

// ---------------------------------------------------------------------------
// Hook
// ---------------------------------------------------------------------------

export function useAnimatedColorTransition(
  stateIndex: number,
  stops: ColorTransitionStops,
  config?: ColorTransitionConfig,
) {
  const duration = config?.duration ?? DEFAULT_DURATION;

  const bgColors = stops.backgroundColor;
  const borderColors = stops.borderColor;

  // Memoise the mutable copies so reference identity is stable across renders
  // as long as the actual colour values haven't changed.
  const bgKey = bgColors?.join(",") ?? "";
  const borderKey = borderColors?.join(",") ?? "";

  const bgIndices = useMemo(() => (bgColors ? Array.from({ length: bgColors.length }, (_, i) => i) : null), [bgKey]);
  const bgMutable = useMemo(() => (bgColors ? Array.from(bgColors) : null), [bgKey]);
  const borderIndices = useMemo(
    () => (borderColors ? Array.from({ length: borderColors.length }, (_, i) => i) : null),
    [borderKey],
  );
  const borderMutable = useMemo(() => (borderColors ? Array.from(borderColors) : null), [borderKey]);

  const progress = useDerivedValue(
    () => withTiming(stateIndex, { duration, easing: DEFAULT_EASING }),
    [stateIndex, duration],
  );

  return useAnimatedStyle(() => {
    "worklet";
    const style: { backgroundColor?: string; borderColor?: string } = {};
    if (bgIndices && bgMutable) {
      style.backgroundColor = interpolateColor(progress.value, bgIndices, bgMutable);
    }
    if (borderIndices && borderMutable) {
      style.borderColor = interpolateColor(progress.value, borderIndices, borderMutable);
    }
    return style;
  }, [bgIndices, bgMutable, borderIndices, borderMutable]);
}
